import React, { useCallback, useMemo, useState } from 'react';
import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { history, toTitleCase } from '../../utils';
import AddIcon from '@mui/icons-material/Add';
import { MovaticFieldGroupAsyncSelect } from '../../components/Redesing/movatic-field-group';
import { taskColumnsV2 } from '../../tableColumns/maintenanceColumns';
import {
  IColumn,
  ISortConfig,
} from '../../ts/interface/components/redesing/table.interface';
import { useMaintenanceLogsRelayQuery } from '../../hooks/maintenance/query/V2/useMaintenanceLogsRelay';
import { mapMaintenanceLogsToLegacy } from '../../hooks/maintenance/query/V2/maintenanceMappers';
import { connect } from 'react-redux';
import { ISystem } from '../../ts/interface/system.interface';
import { IMode } from '../../ts/interface/mode.interface';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import { SearchChip } from '../../ts/interface/components/table.interface';
import useStation from '../../hooks/stations/query/useStation';
import { IStation } from '../../ts/interface/station.interface';
import useUrlState from '../../hooks/use-url-state';
import { useUpdateEffect } from '../../hooks/use-update-effect';
import { LocationStatus } from '../../ts/enums';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';

interface IMaintenanceLogsProps {
  systemLoaded: boolean;
  system: ISystem;
  mode: IMode;
}

const MaintenanceLogs: React.FC<IMaintenanceLogsProps> = (props) => {
  const { systemLoaded, mode, system } = props;
  const { unitsTitle } = mode.details;
  const { new_hardware } = system;
  const [open, setOpen] = React.useState(false);
  const [locationValues, setLocationValues] = React.useState<{
    value: string;
    label: string;
  }>({ value: '', label: '' });

  const [pageNumber, setPageNumber] = useState(0);
  // Keyset (cursor) pagination state. The backend returns opaque keyset cursors,
  // so we feed the previous page's endCursor into `after` and accumulate pages
  // locally, letting PaginationTable slice the requested page.
  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [accumulatedLogs, setAccumulatedLogs] = useState<any[]>([]);
  const [pageState, setPageState, isComplete, isRestored] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    sortingConfig: {
      key: 'created_on',
      direction: 'desc',
    } as ISortConfig,
    locations: [] as string[],
  });
  const debouncedSearchTerm = useSearchDebounce(pageState.search);
  const [exportData, setExportData] = useState<boolean>(false);

  const [chips, setChips] = useState<SearchChip[]>([]);

  const [searchLocation, setSearchLocation] = useState<string>('');

  // Convert sort config to GraphQL format
  const sortConfig = useMemo(() => {
    const keyMap: Record<string, string> = {
      created_on: 'CREATED_ON',
      task_name: 'MAINTENANCE_TASK_OLD_ID',
      admin_name: 'ADMIN_USER_ID',
      time_tracking: 'TIME_TRACKING',
    };
    const field =
      keyMap[pageState.sortingConfig?.key || 'created_on'] || 'CREATED_ON';
    const order =
      pageState.sortingConfig?.direction?.toUpperCase() === 'DESC'
        ? 'DESC'
        : 'ASC';
    return [{ field: field as any, order: order as 'ASC' | 'DESC' }];
  }, [pageState.sortingConfig]);

  const {
    data: logsDataRaw,
    count: totalLogs,
    isLoading: loadingLogs,
    isRefetching: fetchingLogs,
    hasMore,
    endCursor,
  } = useMaintenanceLogsRelayQuery(
    {
      systemId: system.gid,
      locationId: locationValues.value || undefined,
      search: debouncedSearchTerm || undefined,
    },
    {
      limit: pageState.sizePerPage,
      sort: sortConfig,
      enabled: isComplete && systemLoaded,
      after: afterCursor || undefined,
    }
  );

  // Any change to the query inputs (location, search, sort, page size) invalidates
  // the accumulated cursor pages and returns to the first page.
  const resetPagination = useCallback(() => {
    setAccumulatedLogs([]);
    setAfterCursor(null);
    setPageNumber(0);
  }, []);

  const filteredCompletedTasks = useMemo(
    () => (logsDataRaw ? mapMaintenanceLogsToLegacy(logsDataRaw) : []),
    [logsDataRaw]
  );

  // Accumulate cursor pages so the table can slice the requested page locally.
  // Appends when advancing (afterCursor set); replaces on the first page.
  useMemo(() => {
    if (!filteredCompletedTasks.length && !afterCursor) {
      setAccumulatedLogs([]);
      return;
    }
    if (filteredCompletedTasks.length) {
      if (afterCursor) {
        setAccumulatedLogs((prev) => [...prev, ...filteredCompletedTasks]);
      } else {
        setAccumulatedLogs(filteredCompletedTasks);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCompletedTasks]);

  const logItems =
    accumulatedLogs.length > 0 ? accumulatedLogs : filteredCompletedTasks;

  const { data: stationsData, isLoading: isLoadingLocations } = useStation(
    {
      enabled: systemLoaded && open,
    },
    { status: LocationStatus.active, pattern: searchLocation }
  );

  const stations = useMemo(() => stationsData?.data || [], [stationsData]);

  useUpdateEffect(() => {
    if (isComplete && isRestored) {
      const locationValues: IStation[] = stations.filter((station: IStation) =>
        pageState.locations.includes(station.id.toString())
      );
      const chips = locationValues.map((location) => ({
        label: 'Locations',
        field: 'locations',
        value: location.id.toString(),
        displayValue: location.name,
      }));
      setLocationValues({
        value: locationValues[0]?.id.toString(),
        label: locationValues[0]?.name,
      });
      setChips(chips);
    }
  }, [isRestored]);

  const handleLocationChange = useCallback(
    (values: string[], options: { label: string; value: string }[]): void => {
      setPageNumber(0);
      setChips((prevChips) => {
        const valuesFound: string[] = [];

        const newChips = prevChips.filter((chip) => {
          if (chip.field !== 'locations') {
            return true;
          }

          const found = values.includes(chip.value as string);

          if (found) {
            valuesFound.push(chip.value as string);
          }

          return found;
        });

        if (values.length === valuesFound.length) {
          return newChips;
        }
        values.forEach((value) => {
          if (!valuesFound.includes(value)) {
            const option = options.find((option) => option.value.toString() === value);
            newChips.push({
              label: 'Locations',
              field: 'locations',
              value,
              displayValue: option!.label,
            });
          }
        });
        return newChips;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const stationOptions = useMemo(() => {
    return stations.map((item: IStation) => {
      return {
        value: item.id,
        label: toTitleCase(item.name),
      };
    });
  }, [stations]);

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      setChips((prevChips) => {
        return prevChips.filter((group) => {
          return group.label !== deletedGroup.label;
        });
      });
      resetPagination();
      setLocationValues({ value: '', label: '' });
      setPageState((prevState) => ({
        ...prevState,
        locations: [],
      }));
    },
    [setPageState, resetPagination]
  );

  return (
    <PaginationTable
      showFilter
      totalItems={totalLogs || 0}
      isLoading={
        (loadingLogs && !logItems.length) ||
        !isComplete ||
        (pageNumber > 0 && logItems.length <= pageNumber * pageState.sizePerPage)
      }
      isLoadingCache={fetchingLogs}
      hasMore={hasMore}
      padding={{ pt: 1, pl: 0, pr: 1 }}
      exportData={exportData}
      csvFileName={'Maintenance_logs.csv'}
      onExport={() => setExportData(false)}
      localSearch={false}
      buttons={[
        {
          label: 'Export',
          startIcon: <FileDownloadIcon />,
          onClick: () => setExportData(true),
        },
        {
          label: 'Add',
          onClick: () => history.push('/maintenance/log/add'),
          startIcon: <AddIcon />,
        },
      ]}
      onApplyFilter={() => {
        setPageState((prevState) => ({
          ...prevState,
          locations: [locationValues.value],
        }));
        handleLocationChange(
          [
            ...stationOptions
              .filter((item: { label: string }) => item.label === locationValues.label)
              .map((item: { value: string | number }) => item.value.toString()),
          ],
          stationOptions
        );
      }}
      chips={chips}
      handleChipDelete={handleChipDelete}
      spaceFilters={
        <MovaticFieldGroupAsyncSelect
          dataId="location-single-select"
          label={'Location'}
          description="Select the location to filter the maintenance logs."
          isLoading={isLoadingLocations}
          name={'location_select'}
          id={'location_select'}
          options={stationOptions}
          showClearButton={true}
          value={{
            value: locationValues?.value,
            label: locationValues?.label,
          }}
          onChange={(
            event: React.SyntheticEvent,
            newValue: { value: string; label: string }
          ) => {
            resetPagination();
            setLocationValues(newValue);
          }}
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
          getOptionText={(option: any) => option.label}
          handleSearch={(search) => setSearchLocation(search.target.value)}
        />
      }
      customKey="gid"
      onRowCLick={(id: string) => history.push(`/maintenance/log/${id}`)}
      sortBy={pageState?.sortingConfig?.key}
      onSortChange={(sort) => {
        resetPagination();
        setPageState((prevState) => ({
          ...prevState,
          sortingConfig: sort as ISortConfig,
        }));
      }}
      columns={
        taskColumnsV2(unitsTitle, new_hardware, mode.details.stationsTitle) as IColumn[]
      }
      items={logItems}
      searchPlaceholder={`Search logs`}
      page={pageNumber}
      rowsPerPage={pageState.sizePerPage}
      onPageChange={(page, newPage) => {
        // Advancing past the accumulated pages: fetch the next keyset page.
        if (logItems.length && logItems.length / pageState.sizePerPage < newPage + 1) {
          setAfterCursor(endCursor || null);
        }
        setPageNumber(newPage);
      }}
      onRowsPerPageChange={(pageSizeString) => {
        resetPagination();
        setPageState((prevState) => ({
          ...prevState,
          sizePerPage: Number(pageSizeString.target.value),
        }));
      }}
      handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
        resetPagination();
        setPageState((prevState) => ({
          ...prevState,
          search: e.target.value,
        }));
      }}
      searchText={pageState.search}
    />
  );
};

export default connect(
  (state: { system: { isLoaded: boolean; current: ISystem }; mode: IMode }) => ({
    systemLoaded: state.system.isLoaded,
    system: state.system.current,
    mode: state.mode,
  }),
  () => ({})
)(MaintenanceLogs);
